import { user_roles } from 'BrdData/Enum';
import UserData from 'BrdData/UserData';
import { EditableDetailPanel } from 'Components';
import { ButtonFooter, UndoButton } from 'Components/Buttons';
import { useAppStateContext, useToast, useUserContext } from 'Context';
import joi from 'joi';
import { DataManager } from 'Lib/data';
import {
  useUndoableSaveAction,
  useUpdatableObject,
  useValidation,
} from 'Lib/hooks';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { isUserAuthorized } from 'Util';

let DM = new DataManager();

const UserInformationPanel = ({
  userId,
  userObject,
  title = 'User',
  editable = false,
  ...props
}) => {
  const { appUserId } = useAppStateContext();
  const { showSaveSuccessToast } = useToast();

  const {
    currentObject: user,
    hasBeenUpdated,
    reset: resetUser,
    updateField,
    originalObject: originalUser,
    doSave,
  } = useUpdatableObject(UserData.updateRecord, UserData.createUser, 'USER');

  const [resetUndo, showSaveUndoToast] = useUndoableSaveAction({
    originalObject: originalUser,
    undoFunction: UserData.updateRecord,
    onUndo: (originalUser) => fetchUser(originalUser.id),
    objectType: 'User',
  });

  const [editState, setEditState] = useState(undefined);

  const { fullUserList: currentUsernameList } = useUserContext();

  useEffect(() => {
    DM.add(
      UserData.registerListener('BLANK_RECORD_LOADED', onUserLoaded),
      UserData,
    );
    DM.add(UserData.registerListener('RECORD_LOADED', onUserLoaded), UserData);
    DM.add(
      UserData.registerListener('RECORD_CREATED', onUserCreated),
      UserData,
    );
    DM.add(
      UserData.registerListener('RECORD_UPDATED', onUserUpdated),
      UserData,
    );
    return () => {
      DM.clear();
    };
  }, []);

  const UNIDFormatErrorMessage =
    'User ID should be a university UNID starting with a lowercase u';

  let fields = {
    full_name: {
      label: 'Full Name',
      onEdit: (newValue) => {
        updateField('full_name', newValue);
      },
      isRequired: true,
    },

    ...((isUserAuthorized({
      roles: [user_roles.ADMINISTRATOR],
    }) ||
      user?.id === appUserId) && {
      username: {
        label: 'User ID (UNID)',
        fieldSchema: joi
          .string()
          .label('User ID')
          .min(2)
          .pattern(new RegExp(/^u\d+$/))
          .invalid(
            ...currentUsernameList
              .filter(({ id }) => user?.id !== id)
              .map(({ username }) => username),
          )
          .insensitive()
          .required()
          .messages({
            'string.pattern.base': UNIDFormatErrorMessage,
            'string.empty': 'User ID is required',
            'any.invalid': 'User ID already in use',
            'string.min': UNIDFormatErrorMessage,
          }),
        onEdit: (newValue) => {
          updateField('username', newValue.toLowerCase());
        },
        readOnly: !isUserAuthorized({
          roles: [user_roles.ADMINISTRATOR],
        }),
        isRequired: true,
      },
    }),

    email: {
      label: 'Email',
      onEdit: (newValue) => {
        updateField('email', newValue);
      },
      isRequired: true,
      isEmail: true,
    },

    office_phone: {
      label: 'Office Phone',
      value: user.phone_numbers?.office,
      onEdit: (newValue) => {
        updateField('phone_numbers', {
          ...user.phone_numbers,
          office: newValue,
        });
      },
    },

    ...((isUserAuthorized({
      roles: [user_roles.ADMINISTRATOR],
    }) ||
      user?.id === appUserId) && {
      cell_phone: {
        label: 'Cell Phone',
        value: user.phone_numbers?.cell,
        onEdit: (newValue) => {
          updateField('phone_numbers', {
            ...user.phone_numbers,
            cell: newValue,
          });
        },
      },
    }),

    ...(isUserAuthorized({
      roles: [user_roles.ADMINISTRATOR],
    }) && {
      notes: {
        label: 'Notes',
        value: user.notes,
        onEdit: (newValue) => {
          updateField('notes', newValue);
        },
      },
    }),
  };

  let { resetValidation, getValidatedDetailTable, ValidatedSaveButton } =
    useValidation(fields, user);

  useEffect(() => {
    if (userId && userId >= 0) {
      fetchUser(userId);
    }
  }, [userId]);

  useEffect(() => {
    if (userObject) {
      if (!editable) {
        resetUser(userObject);
      } else if (userObject?.id && Number(userObject.id) >= 0) {
        fetchUser(userObject.id);
      }
    }
  }, [userObject]);

  const onUserLoaded = (payload) => {
    if (payload) {
      resetUser(payload);
      resetUndo(() => fetchUser(payload.id));
      resetValidation();
    }
  };

  const fetchUser = (id) => {
    UserData.getRecord(id, {
      _include: 'addresses',
    });
  };

  const onUserUpdated = (payload) => {
    if (payload) {
      showSaveUndoToast();
      setEditState(false);
    }
  };

  const onUserCreated = (payload) => {
    if (payload) {
      showSaveSuccessToast('User Created');
      fetchUser(payload.id);
      setEditState(false);
    }
  };

  const undoAction = () => {
    fetchUser(user.id);
    resetValidation();
  };

  const undoButton = editable && hasBeenUpdated && (
    <UndoButton onClick={undoAction} />
  );

  const saveButton = editable && hasBeenUpdated && (
    <ValidatedSaveButton doSave={doSave} />
  );

  const footer = (
    <ButtonFooter>
      {undoButton}
      {saveButton}
    </ButtonFooter>
  );

  return (
    <EditableDetailPanel
      title={title}
      footer={footer}
      editable={editable}
      collapsible
      renderChild={getValidatedDetailTable}
      parentEditState={editState}
      {...props}
    />
  );
};

UserInformationPanel.propTypes = {
  userId: PropTypes.number,
  userObject: PropTypes.object,
  title: PropTypes.string,
  editable: PropTypes.bool,
  hideNotes: PropTypes.bool,
};

export default UserInformationPanel;
